/**
 * Academy Module - Hive Dashboard Integration
 * Shows enrolled courses, allows access code redemption, and tracks progress
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  GraduationCap, Lock, Unlock, BookOpen, CheckCircle, 
  Play, Clock, ChevronRight, ChevronLeft, AlertCircle, Key, Trophy,
  Truck, Headphones, Briefcase, Building2, ArrowRight,
  Download, FileText, HelpCircle, X, Check, RefreshCw, Award, Sparkles, Rocket, Users, Film,
  Lightbulb, Target, AlertTriangle, Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { COURSES } from '../../courseData';
import { COURSE_RESOURCES, MODULE_QUIZZES, calculateQuizScore } from '../../academyResources';
import { getEnhancedLesson, hasEnhancedContent } from '../../data/courses';
import EntrepreneurshipModule from './EntrepreneurshipModule';
import CommunityModule from './CommunityModule';
import { CourseVideoSection } from './AcademyVideoPlayer';
import Certificate from './Certificate';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping
const ICON_MAP = {
  Truck,
  Headphones,
  Briefcase,
  Building2
};

// Enhanced Lesson Content Component - Displays rich course content with exercises, tips, and takeaways
const EnhancedLessonContent = ({ 
  course, 
  activeModule, 
  activeLesson, 
  setActiveLesson,
  isLessonCompleted,
  handleCompleteLesson,
  completingLesson
}) => {
  const moduleId = course.modules[activeModule]?.id;
  const enhancedData = getEnhancedLesson(course.id, moduleId, activeLesson.id);
  const hasEnhanced = !!enhancedData;
  
  // State for exercise responses
  const [exerciseResponses, setExerciseResponses] = useState({});
  
  // Load saved responses from localStorage on mount
  useEffect(() => {
    const storageKey = `exercise_${course.id}_${activeLesson.id}`;
    const savedResponses = localStorage.getItem(storageKey);
    if (savedResponses) {
      try {
        setExerciseResponses(JSON.parse(savedResponses));
      } catch (e) {
        console.error('Failed to parse saved responses:', e);
      }
    }
  }, [course.id, activeLesson.id]);
  
  // Save responses to localStorage when they change
  useEffect(() => {
    if (Object.keys(exerciseResponses).length > 0) {
      const storageKey = `exercise_${course.id}_${activeLesson.id}`;
      localStorage.setItem(storageKey, JSON.stringify(exerciseResponses));
    }
  }, [exerciseResponses, course.id, activeLesson.id]);
  
  // Handle input change for exercise fields
  const handleExerciseInput = (fieldId, value) => {
    setExerciseResponses(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };
  
  // Use enhanced content if available, otherwise use base content
  const lessonTitle = enhancedData?.title || activeLesson.title;
  const lessonDuration = enhancedData?.duration || activeLesson.duration;
  const lessonContent = enhancedData?.content || activeLesson.content;
  const exercises = enhancedData?.exercises || [];
  const keyTakeaways = enhancedData?.keyTakeaways || [];
  const proTips = enhancedData?.proTips || [];
  const commonMistakes = enhancedData?.commonMistakes || [];

  // Convert underscores/blanks to input fields
  const renderInteractiveText = (text, lineIdx) => {
    // Pattern to match answer blanks: underscores (3+ _), $______, ___/10, etc.
    const blankPattern = /(\$?_{3,}[^\s]*|_{3,})/g;
    const parts = text.split(blankPattern);
    
    return parts.map((part, partIdx) => {
      if (part.match(blankPattern)) {
        const fieldId = `field_${lineIdx}_${partIdx}`;
        const isMoneyField = part.startsWith('$');
        const hasUnit = part.match(/_{3,}(.+)/);
        const unit = hasUnit ? hasUnit[1] : '';
        
        // Determine input width based on blank length
        const blankLength = (part.match(/_/g) || []).length;
        const inputWidth = Math.min(Math.max(blankLength * 10, 60), 200);
        
        return (
          <span key={partIdx} className="inline-flex items-center gap-1">
            {isMoneyField && <span className="text-[#D4AF37]">$</span>}
            <input
              type="text"
              value={exerciseResponses[fieldId] || ''}
              onChange={(e) => handleExerciseInput(fieldId, e.target.value)}
              className="bg-white/5 border-b-2 border-[#D4AF37]/50 focus:border-[#D4AF37] outline-none px-2 py-0.5 text-white text-sm transition-colors"
              style={{ width: `${inputWidth}px` }}
              placeholder=""
              data-testid={`exercise-input-${fieldId}`}
            />
            {unit && <span className="text-white/70">{unit}</span>}
          </span>
        );
      }
      // Return regular text with inline formatting
      return <span key={partIdx} dangerouslySetInnerHTML={{ __html: formatInlineTextWithInputs(part) }} />;
    });
  };
  
  // Format inline text but preserve structure for input handling
  const formatInlineTextWithInputs = (text) => {
    return text
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="bg-white/10 px-1 rounded text-[#D4AF37]">$1</code>');
  };

  // Render markdown-like content with better formatting
  const renderContent = (content) => {
    if (!content) return null;
    
    // Convert markdown headers, bold, and lists to styled elements
    const lines = content.split('\n');
    let inTable = false;
    let tableRows = [];
    
    return lines.map((line, idx) => {
      // Handle tables with interactive cells
      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(line);
        
        // Check if next line is not a table or end of content
        const nextLine = lines[idx + 1];
        if (!nextLine || !nextLine.startsWith('|')) {
          inTable = false;
          // Skip separator rows
          const dataRows = tableRows.filter(row => !row.includes('---'));
          const headers = dataRows[0]?.split('|').filter(cell => cell.trim());
          const bodyRows = dataRows.slice(1);
          
          return (
            <div key={idx} className="overflow-x-auto my-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#D4AF37]/10">
                    {headers?.map((header, hIdx) => (
                      <th key={hIdx} className="border border-white/10 px-3 py-2 text-left text-white font-medium">
                        {header.trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-white/5">
                      {row.split('|').filter(cell => cell.trim()).map((cell, cIdx) => {
                        const cellContent = cell.trim();
                        const hasBlank = cellContent.match(/_{3,}/);
                        return (
                          <td key={cIdx} className="border border-white/10 px-3 py-2 text-white/70">
                            {hasBlank ? renderInteractiveText(cellContent, `table_${idx}_${rIdx}_${cIdx}`) : cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      }
      
      if (inTable) return null;
      
      // H1 headers
      if (line.startsWith('# ')) {
        return (
          <h1 key={idx} className="text-2xl font-bold text-[#D4AF37] mt-6 mb-4">
            {line.replace('# ', '')}
          </h1>
        );
      }
      
      // H2 headers
      if (line.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-xl font-bold text-white mt-6 mb-3 border-b border-white/10 pb-2">
            {line.replace('## ', '')}
          </h2>
        );
      }
      
      // H3 headers
      if (line.startsWith('### ')) {
        return (
          <h3 key={idx} className="text-lg font-semibold text-white/90 mt-4 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      }
      
      // Horizontal rule
      if (line.trim() === '---') {
        return <hr key={idx} className="border-white/10 my-6" />;
      }
      
      // Blockquotes
      if (line.startsWith('> ')) {
        return (
          <blockquote key={idx} className="border-l-4 border-[#D4AF37] pl-4 my-4 italic text-white/70 bg-[#D4AF37]/5 py-2">
            {line.replace('> ', '')}
          </blockquote>
        );
      }
      
      // Checkboxes
      if (line.match(/^-\s*\[[\sx]\]/)) {
        const checked = line.includes('[x]');
        const text = line.replace(/^-\s*\[[\sx]\]\s*/, '');
        return (
          <div key={idx} className="flex items-start gap-2 my-1 ml-2">
            <div className={`w-4 h-4 border rounded flex-shrink-0 mt-0.5 ${checked ? 'bg-green-500 border-green-500' : 'border-white/30'}`}>
              {checked && <Check className="w-4 h-4 text-white" />}
            </div>
            <span className="text-white/70">{text}</span>
          </div>
        );
      }
      
      // Bullet lists - check for blanks
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const text = line.replace(/^[-*]\s/, '');
        const hasBlank = text.match(/_{3,}/);
        return (
          <div key={idx} className="flex items-start gap-2 my-1 ml-2">
            <span className="text-[#D4AF37] mt-1">•</span>
            <span className="text-white/70 flex-1">
              {hasBlank ? renderInteractiveText(text, idx) : <span dangerouslySetInnerHTML={{ __html: formatInlineTextWithInputs(text) }} />}
            </span>
          </div>
        );
      }
      
      // Numbered lists - check for blanks
      if (line.match(/^\d+\.\s/)) {
        const num = line.match(/^(\d+)\./)[1];
        const text = line.replace(/^\d+\.\s/, '');
        const hasBlank = text.match(/_{3,}/);
        return (
          <div key={idx} className="flex items-start gap-2 my-1 ml-2">
            <span className="text-[#D4AF37] font-medium">{num}.</span>
            <span className="text-white/70 flex-1">
              {hasBlank ? renderInteractiveText(text, idx) : <span dangerouslySetInnerHTML={{ __html: formatInlineTextWithInputs(text) }} />}
            </span>
          </div>
        );
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <div key={idx} className="h-2" />;
      }
      
      // Code blocks
      if (line.startsWith('```')) {
        return null; // Skip code fence markers
      }
      
      // Regular paragraphs - check for blanks
      const hasBlank = line.match(/_{3,}/);
      if (hasBlank) {
        return (
          <p key={idx} className="text-white/80 my-2">
            {renderInteractiveText(line, idx)}
          </p>
        );
      }
      
      return (
        <p key={idx} className="text-white/80 my-2" dangerouslySetInnerHTML={{ __html: formatInlineTextWithInputs(line) }} />
      );
    });
  };
  
  // Format inline text (bold, italic, code)
  const formatInlineText = (text) => {
    return text
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="bg-white/10 px-1 rounded text-[#D4AF37]">$1</code>')
      .replace(/💡/g, '<span class="text-yellow-400">💡</span>')
      .replace(/✅/g, '<span class="text-green-400">✅</span>')
      .replace(/🚩/g, '<span class="text-red-400">🚩</span>');
  };
  
  // Count how many fields have been filled
  const filledFieldsCount = Object.values(exerciseResponses).filter(v => v && v.trim()).length;
  const hasExerciseResponses = filledFieldsCount > 0;

  return (
    <div>
      {/* Mobile Back Button */}
      <button
        onClick={() => setActiveLesson(null)}
        className="md:hidden flex items-center gap-2 text-[#D4AF37] text-sm mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Lessons
      </button>
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider">
              Module {activeModule + 1}: {course.modules[activeModule].title}
            </p>
            {hasEnhanced && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full">
                <Sparkles className="w-3 h-3" />
                Enhanced
              </span>
            )}
          </div>
          <h3 className="text-lg md:text-xl font-bold text-white">{lessonTitle}</h3>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-sm">
          <Clock className="w-4 h-4" />
          {lessonDuration}
        </div>
      </div>

      {/* Main Lesson Content */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6 mb-6">
        <div className="prose prose-invert max-w-none">
          {renderContent(lessonContent)}
        </div>
        
        {/* Exercise Progress Indicator - shows when user has started filling in blanks */}
        {hasExerciseResponses && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-white/70">
                  {filledFieldsCount} field{filledFieldsCount !== 1 ? 's' : ''} completed
                </span>
                <span className="text-white/40 text-xs">(auto-saved)</span>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('Clear all your exercise responses? This cannot be undone.')) {
                    setExerciseResponses({});
                    const storageKey = `exercise_${course.id}_${activeLesson.id}`;
                    localStorage.removeItem(storageKey);
                  }
                }}
                className="text-xs text-white/40 hover:text-red-400 transition-colors"
              >
                Clear Responses
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Key Takeaways */}
      {keyTakeaways.length > 0 && (
        <div className="bg-green-500/5 border border-green-500/20 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-green-400" />
            <h4 className="text-green-400 font-semibold">Key Takeaways</h4>
          </div>
          <ul className="space-y-2">
            {keyTakeaways.map((takeaway, idx) => (
              <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                {takeaway}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pro Tips */}
      {proTips.length > 0 && (
        <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
            <h4 className="text-[#D4AF37] font-semibold">Pro Tips from Industry Veterans</h4>
          </div>
          <ul className="space-y-2">
            {proTips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                <Star className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Common Mistakes */}
      {commonMistakes.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h4 className="text-red-400 font-semibold">Common Mistakes to Avoid</h4>
          </div>
          <ul className="space-y-2">
            {commonMistakes.map((mistake, idx) => (
              <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                {mistake}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Exercises */}
      {exercises.length > 0 && (
        <div className="bg-blue-500/5 border border-blue-500/20 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-blue-400" />
            <h4 className="text-blue-400 font-semibold">Practice Exercises</h4>
          </div>
          <div className="space-y-4">
            {exercises.map((exercise, idx) => (
              <div key={idx} className="bg-[#0A0A0A] p-3 border border-blue-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-400 text-xs font-mono uppercase px-2 py-0.5 bg-blue-500/10 rounded">
                    {exercise.type}
                  </span>
                  {exercise.timeEstimate && (
                    <span className="text-white/40 text-xs">~{exercise.timeEstimate}</span>
                  )}
                </div>
                <h5 className="text-white font-medium text-sm mb-1">{exercise.title || `Exercise ${idx + 1}`}</h5>
                <p className="text-white/70 text-sm">{exercise.prompt}</p>
                {exercise.hint && (
                  <p className="text-white/40 text-xs mt-2 italic">Hint: {exercise.hint}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mark Complete Button */}
      <div className="mt-6 flex justify-end">
        {isLessonCompleted(activeLesson.id) ? (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            Lesson Completed
          </div>
        ) : (
          <button
            onClick={() => handleCompleteLesson(activeLesson.id)}
            disabled={completingLesson}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-bold uppercase text-sm hover:bg-[#B8962E] disabled:opacity-50"
            data-testid="mark-complete-btn"
          >
            {completingLesson ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Mark as Complete
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const AcademyModule = ({ userId, userEmail }) => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessCode, setAccessCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Fetch user's enrolled courses
  const fetchEnrolledCourses = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/my-courses/${userId}`);
      setEnrolledCourses(response.data.courses || []);
    } catch (err) {
      console.error('Failed to fetch enrolled courses:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchEnrolledCourses();
  }, [fetchEnrolledCourses]);

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!accessCode.trim() || !userId) return;

    setRedeemLoading(true);
    setRedeemError('');
    setRedeemSuccess('');

    try {
      const response = await axios.post(`${API}/academy/redeem-code`, {
        access_code: accessCode.trim().toUpperCase(),
        user_id: userId
      });

      if (response.data.success) {
        setRedeemSuccess(`Successfully unlocked ${response.data.courses.length} course(s)!`);
        setAccessCode('');
        fetchEnrolledCourses(); // Refresh the list
      }
    } catch (err) {
      setRedeemError(err.response?.data?.detail || 'Failed to redeem code');
    } finally {
      setRedeemLoading(false);
    }
  };

  // Check if a course is enrolled
  const isEnrolled = (courseId) => {
    return enrolledCourses.some(e => e.course_id === courseId);
  };

  // Get enrollment data for a course
  const getEnrollment = (courseId) => {
    return enrolledCourses.find(e => e.course_id === courseId);
  };

  // Calculate progress percentage
  const calculateProgress = (enrollment, course) => {
    if (!enrollment || !course) return 0;
    const totalLessons = course.modules.reduce((acc, m) => acc + m.lessons.length, 0);
    const completedLessons = enrollment.completed_lessons?.length || 0;
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="academy-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#D4AF37]/20">
            <GraduationCap className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase">
              Queen B Academy
            </h2>
            <p className="text-white/50 text-sm">
              {enrolledCourses.length > 0 
                ? `${enrolledCourses.length} course${enrolledCourses.length > 1 ? 's' : ''} enrolled`
                : 'No courses enrolled yet'}
            </p>
          </div>
        </div>
        <Link
          to="/academy"
          className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
          data-testid="browse-courses-link"
        >
          Browse Courses <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Access Code Redemption */}
      <div className="bg-[#0A0A0A] border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="text-white font-medium text-sm">Redeem Access Code</h3>
        </div>
        
        <form onSubmit={handleRedeemCode} className="flex gap-2">
          <input
            type="text"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
            placeholder="QBA-XXXXX"
            className="flex-1 bg-[#121212] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none font-mono"
            data-testid="access-code-input"
          />
          <button
            type="submit"
            disabled={redeemLoading || !accessCode.trim()}
            className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-bold uppercase hover:bg-[#B8962E] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="redeem-code-btn"
          >
            {redeemLoading ? '...' : 'Redeem'}
          </button>
        </form>

        {redeemError && (
          <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {redeemError}
          </div>
        )}
        
        {redeemSuccess && (
          <div className="flex items-center gap-2 mt-2 text-green-400 text-sm">
            <CheckCircle className="w-4 h-4" />
            {redeemSuccess}
          </div>
        )}
      </div>

      {/* Course Library */}
      <div className="space-y-3">
        <h3 className="text-white/60 text-xs uppercase tracking-wider">Course Library</h3>
        
        <div className="grid gap-3">
          {COURSES.map(course => {
            const enrolled = isEnrolled(course.id);
            const enrollment = getEnrollment(course.id);
            const progress = calculateProgress(enrollment, course);
            const IconComponent = ICON_MAP[course.icon] || BookOpen;

            return (
              <div
                key={course.id}
                className={`bg-[#0A0A0A] border transition-colors ${
                  enrolled 
                    ? 'border-[#D4AF37]/30 hover:border-[#D4AF37]/50' 
                    : 'border-white/5 hover:border-white/10'
                }`}
                data-testid={`course-card-${course.id}`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2"
                        style={{ backgroundColor: enrolled ? `${course.color}20` : 'rgba(255,255,255,0.05)' }}
                      >
                        <IconComponent 
                          className="w-5 h-5" 
                          style={{ color: enrolled ? course.color : 'rgba(255,255,255,0.3)' }} 
                        />
                      </div>
                      <div>
                        <h4 className={`font-medium ${enrolled ? 'text-white' : 'text-white/50'}`}>
                          {course.title}
                        </h4>
                        <p className="text-white/40 text-xs">{course.modules.length} modules</p>
                      </div>
                    </div>
                    
                    {enrolled ? (
                      <div className="flex items-center gap-1 text-green-400 text-xs">
                        <Unlock className="w-4 h-4" />
                        Unlocked
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-white/30 text-xs">
                        <Lock className="w-4 h-4" />
                        Locked
                      </div>
                    )}
                  </div>

                  {enrolled ? (
                    <>
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-white/60">Progress</span>
                          <span className="text-[#D4AF37]">{progress}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10 overflow-hidden">
                          <div 
                            className="h-full bg-[#D4AF37] transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => setSelectedCourse(course)}
                        className="w-full flex items-center justify-center gap-2 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/20 transition-colors"
                        data-testid={`start-course-${course.id}`}
                      >
                        {progress > 0 ? (
                          <>
                            <Play className="w-4 h-4" />
                            Continue Learning
                          </>
                        ) : (
                          <>
                            <BookOpen className="w-4 h-4" />
                            Start Course
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/academy"
                      className="w-full flex items-center justify-center gap-2 py-2 bg-white/5 border border-white/10 text-white/50 text-sm hover:bg-white/10 transition-colors"
                    >
                      <Key className="w-4 h-4" />
                      Get Access - ${course.price}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* No Courses Message */}
      {enrolledCourses.length === 0 && (
        <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-6 text-center">
          <GraduationCap className="w-12 h-12 text-[#D4AF37]/50 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-2">No Courses Yet</h3>
          <p className="text-white/50 text-sm mb-4">
            Unlock courses by redeeming an access code or purchase from the Academy.
          </p>
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-sm font-bold uppercase hover:bg-[#B8962E] transition-colors"
            data-testid="explore-academy-btn"
          >
            <GraduationCap className="w-4 h-4" />
            Explore Academy
          </Link>
        </div>
      )}

      {/* Course Content Modal */}
      {selectedCourse && (
        <CourseContentModal 
          course={selectedCourse} 
          enrollment={getEnrollment(selectedCourse.id)}
          userId={userId}
          userEmail={userEmail}
          onClose={() => setSelectedCourse(null)}
          onProgressUpdate={fetchEnrolledCourses}
        />
      )}
    </div>
  );
};

// Course Content Modal with Lessons, Resources, and Quiz tabs
const CourseContentModal = ({ course, enrollment, userId, userEmail, onClose, onProgressUpdate }) => {
  const [activeTab, setActiveTab] = useState('lessons'); // 'lessons', 'resources', 'quiz'
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(null);
  const [completingLesson, setCompletingLesson] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [passedModules, setPassedModules] = useState(enrollment?.passed_modules || []);
  const [bestScores, setBestScores] = useState(enrollment?.best_quiz_scores || {});
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const IconComponent = ICON_MAP[course.icon] || BookOpen;

  const resources = COURSE_RESOURCES[course.id] || [];
  const currentModuleQuiz = MODULE_QUIZZES[course.modules[activeModule]?.id];

  const isLessonCompleted = (lessonId) => {
    return enrollment?.completed_lessons?.includes(lessonId);
  };

  const isModulePassed = (moduleId) => {
    return passedModules.includes(moduleId);
  };

  const handleCompleteLesson = async (lessonId) => {
    if (!userId || completingLesson) return;
    
    setCompletingLesson(true);
    try {
      await axios.post(
        `${API}/academy/update-progress/${userId}/${course.id}?lesson_id=${lessonId}&completed=true`
      );
      onProgressUpdate();
    } catch (err) {
      console.error('Failed to update progress:', err);
    } finally {
      setCompletingLesson(false);
    }
  };

  const handleQuizSubmit = async () => {
    const moduleId = course.modules[activeModule].id;
    const result = calculateQuizScore(moduleId, quizAnswers);
    setQuizResult(result);
    setShowQuizResults(true);
    
    // Save quiz result to backend
    if (userId) {
      setSubmittingQuiz(true);
      try {
        const response = await axios.post(`${API}/academy/submit-quiz/${userId}/${course.id}`, {
          module_id: moduleId,
          answers: quizAnswers,
          score: result.score,
          passed: result.passed,
          total_questions: result.total,
          correct_answers: result.correct
        });
        
        if (response.data.success) {
          // Update local state with new best score
          setBestScores(prev => ({
            ...prev,
            [moduleId]: response.data.best_score || result.score
          }));
          
          if (result.passed && !passedModules.includes(moduleId)) {
            setPassedModules(prev => [...prev, moduleId]);
          }
          
          onProgressUpdate();
        }
      } catch (err) {
        console.error('Failed to save quiz result:', err);
      } finally {
        setSubmittingQuiz(false);
      }
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
    setShowQuizResults(false);
  };

  const handleGetCertificate = async () => {
    setGeneratingCertificate(true);
    try {
      // Get student name from enrollment or user email
      const studentName = enrollment?.student_name || userEmail?.split('@')[0] || 'Student';
      
      const response = await axios.post(`${API}/certificates/generate`, {
        user_id: userId,
        course_id: course.id,
        course_name: course.title,
        student_name: studentName
      });
      
      if (response.data) {
        setCertificateData({
          studentName: response.data.student_name,
          courseName: response.data.course_name,
          courseId: response.data.course_id,
          completionDate: response.data.completion_date,
          certificateId: response.data.certificate_id
        });
        setShowCertificate(true);
      }
    } catch (error) {
      console.error('Failed to generate certificate:', error);
      alert('Failed to generate certificate. Please try again.');
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const totalLessons = course.modules.reduce((acc, m) => acc + m.lessons.length, 0);
  const completedLessons = enrollment?.completed_lessons?.length || 0;
  const progressPercent = Math.round((completedLessons / totalLessons) * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4" data-testid="course-content-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full h-full md:h-auto md:max-w-5xl md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-white/10 bg-[#0A0A0A]">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="p-1.5 md:p-2 flex-shrink-0" style={{ backgroundColor: `${course.color}20` }}>
              <IconComponent className="w-5 h-5 md:w-6 md:h-6" style={{ color: course.color }} />
            </div>
            <div className="min-w-0">
              <h2 className="font-['Barlow_Condensed'] text-base md:text-xl font-bold text-white uppercase truncate">
                {course.title}
              </h2>
              <div className="flex items-center gap-2 md:gap-4 text-xs text-white/50">
                <span>{completedLessons}/{totalLessons} lessons</span>
                <span>{progressPercent}% complete</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white text-2xl p-1 flex-shrink-0"
          >
            &times;
          </button>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="flex border-b border-white/10 bg-[#080808] overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('lessons')}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'lessons'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-lessons"
          >
            <BookOpen className="w-4 h-4" />
            <span>Lessons</span>
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'resources'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-resources"
          >
            <Download className="w-4 h-4" />
            <span>Files</span>
            <span className="hidden md:inline">({resources.length})</span>
          </button>
          <button
            onClick={() => { setActiveTab('quiz'); resetQuiz(); }}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'quiz'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-quiz"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Quiz</span>
          </button>
          <button
            onClick={() => setActiveTab('business')}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'business'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-business"
          >
            <Rocket className="w-4 h-4" />
            <span>Biz</span>
          </button>
          <button
            onClick={() => setActiveTab('community')}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'community'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-community"
          >
            <Users className="w-4 h-4" />
            <span>Forum</span>
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 md:px-6 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'videos'
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] bg-[#D4AF37]/5'
                : 'text-white/50 hover:text-white'
            }`}
            data-testid="tab-videos"
          >
            <Film className="w-4 h-4" />
            <span>Videos</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row">
          {activeTab === 'lessons' && (
            <>
              {/* Sidebar - Module List (hidden on mobile when lesson is active) */}
              <div className={`${activeLesson ? 'hidden md:block' : 'block'} w-full md:w-72 border-r border-white/10 md:overflow-y-auto bg-[#080808] flex-shrink-0`}>
                {course.modules.map((module, idx) => (
                  <div key={module.id}>
                    <button
                      onClick={() => {
                        setActiveModule(idx);
                        setActiveLesson(null);
                      }}
                      className={`w-full text-left p-4 border-b border-white/5 transition-colors ${
                        activeModule === idx 
                          ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' 
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#D4AF37] text-xs font-mono">M{idx + 1}</span>
                        <span className={`text-sm font-medium ${activeModule === idx ? 'text-white' : 'text-white/70'}`}>
                          {module.title}
                        </span>
                      </div>
                      <p className="text-white/40 text-xs">{module.lessons.length} lessons</p>
                    </button>

                    {/* Lessons under active module */}
                    {activeModule === idx && (
                      <div className="bg-[#0A0A0A]">
                        {module.lessons.map((lesson) => {
                          const completed = isLessonCompleted(lesson.id);
                          return (
                            <button
                              key={lesson.id}
                              onClick={() => setActiveLesson(lesson)}
                              className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-center gap-2 transition-colors ${
                                activeLesson?.id === lesson.id
                                  ? 'bg-[#D4AF37]/5'
                                  : 'hover:bg-white/5'
                              }`}
                            >
                              {completed ? (
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <Play className="w-4 h-4 text-white/30 flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs truncate ${completed ? 'text-white/70' : 'text-white/50'}`}>
                                  {lesson.title}
                                </p>
                                <p className="text-white/30 text-xs">{lesson.duration}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Main Content Area (full width on mobile when lesson is active) */}
              <div className={`${activeLesson ? 'block' : 'hidden md:block'} flex-1 overflow-y-auto p-4 md:p-6`}>
                {activeLesson ? (
                  <EnhancedLessonContent 
                    course={course}
                    activeModule={activeModule}
                    activeLesson={activeLesson}
                    setActiveLesson={setActiveLesson}
                    isLessonCompleted={isLessonCompleted}
                    handleCompleteLesson={handleCompleteLesson}
                    completingLesson={completingLesson}
                  />
                ) : (
                  <div className="hidden md:block text-center py-12">
                    <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <h3 className="text-white text-lg font-medium mb-2">
                      Select a Lesson
                    </h3>
                    <p className="text-white/50 text-sm">
                      Choose a lesson from the sidebar to start learning
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'resources' && (
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">Downloadable Resources</h3>
                <p className="text-white/50 text-sm">
                  Checklists, templates, and study materials to help you succeed.
                </p>
              </div>

              {resources.length > 0 ? (
                <div className="grid gap-3 md:gap-4">
                  {resources.map((resource) => (
                    <div
                      key={resource.id}
                      className="bg-[#0A0A0A] border border-white/10 p-3 md:p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:border-[#D4AF37]/30 transition-colors"
                      data-testid={`resource-${resource.id}`}
                    >
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className="p-2 md:p-3 bg-[#D4AF37]/10 flex-shrink-0">
                          <FileText className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-white font-medium text-sm md:text-base truncate">{resource.title}</h4>
                          <p className="text-white/50 text-xs md:text-sm line-clamp-1">{resource.description}</p>
                          <span className="text-white/30 text-xs uppercase">{resource.type}</span>
                        </div>
                      </div>
                      <button
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/20 transition-colors w-full md:w-auto flex-shrink-0"
                        onClick={() => alert(`Download: ${resource.filename}\n\n(Demo - actual file would download here)`)}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-white/50">
                  No resources available for this course yet.
                </div>
              )}
            </div>
          )}

          {activeTab === 'quiz' && (
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Module Selector for Quiz */}
              <div className="mb-6">
                <label className="text-white/50 text-sm mb-2 block">Select Module Quiz:</label>
                <select
                  value={activeModule}
                  onChange={(e) => { setActiveModule(Number(e.target.value)); resetQuiz(); }}
                  className="bg-[#121212] border border-white/10 px-4 py-2 text-white w-full"
                >
                  {course.modules.map((module, idx) => (
                    <option key={module.id} value={idx}>
                      {isModulePassed(module.id) ? '✓ ' : ''}Module {idx + 1}: {module.title}
                    </option>
                  ))}
                </select>
                
                {/* Module Status Badge */}
                {isModulePassed(course.modules[activeModule]?.id) && (
                  <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Module Passed</span>
                    {bestScores[course.modules[activeModule]?.id] && (
                      <span className="text-white/50">• Best Score: {bestScores[course.modules[activeModule].id]}%</span>
                    )}
                  </div>
                )}
              </div>

              {currentModuleQuiz ? (
                showQuizResults ? (
                  /* Quiz Results */
                  <div className="max-w-2xl">
                    <div className={`p-6 mb-6 text-center ${quizResult?.passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      {submittingQuiz && (
                        <div className="mb-2 text-white/50 text-sm">Saving result...</div>
                      )}
                      <div className={`text-5xl font-bold mb-2 ${quizResult?.passed ? 'text-green-400' : 'text-red-400'}`}>
                        {quizResult?.score}%
                      </div>
                      <p className={`text-lg ${quizResult?.passed ? 'text-green-400' : 'text-red-400'}`}>
                        {quizResult?.passed ? 'Congratulations! You passed!' : 'Keep studying and try again!'}
                      </p>
                      <p className="text-white/50 text-sm mt-2">
                        {quizResult?.correct} of {quizResult?.total} correct (Passing: {quizResult?.passingScore}%)
                      </p>
                    </div>

                    {/* Show answers */}
                    <div className="space-y-4 mb-6">
                      {currentModuleQuiz.questions.map((q, idx) => {
                        const isCorrect = quizAnswers[idx] === q.correctAnswer;
                        return (
                          <div key={q.id} className="bg-[#0A0A0A] border border-white/10 p-4">
                            <div className="flex items-start gap-2 mb-2">
                              {isCorrect ? (
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                              )}
                              <p className="text-white">{q.question}</p>
                            </div>
                            <p className="text-white/50 text-sm ml-7">
                              Correct answer: {q.options[q.correctAnswer]}
                            </p>
                            {q.explanation && (
                              <p className="text-[#D4AF37]/70 text-sm ml-7 mt-2">{q.explanation}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <button
                      onClick={resetQuiz}
                      className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-bold uppercase text-sm hover:bg-[#B8962E]"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retake Quiz
                    </button>
                  </div>
                ) : (
                  /* Quiz Questions */
                  <div className="max-w-2xl">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-white">{currentModuleQuiz.title}</h3>
                      <p className="text-white/50 text-sm">
                        {currentModuleQuiz.questions.length} questions | Passing score: {currentModuleQuiz.passingScore}%
                      </p>
                    </div>

                    <div className="space-y-6">
                      {currentModuleQuiz.questions.map((q, qIdx) => (
                        <div key={q.id} className="bg-[#0A0A0A] border border-white/10 p-4" data-testid={`quiz-question-${qIdx}`}>
                          <p className="text-white mb-4">
                            <span className="text-[#D4AF37] mr-2">{qIdx + 1}.</span>
                            {q.question}
                          </p>
                          <div className="space-y-2">
                            {q.options.map((option, optIdx) => (
                              <button
                                key={optIdx}
                                onClick={() => setQuizAnswers(prev => ({ ...prev, [qIdx]: optIdx }))}
                                className={`w-full text-left p-3 border transition-colors ${
                                  quizAnswers[qIdx] === optIdx
                                    ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-white'
                                    : 'bg-[#121212] border-white/10 text-white/70 hover:border-white/30'
                                }`}
                              >
                                <span className="text-white/40 mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 flex items-center justify-between">
                      <p className="text-white/50 text-sm">
                        {Object.keys(quizAnswers).length} of {currentModuleQuiz.questions.length} answered
                      </p>
                      <button
                        onClick={handleQuizSubmit}
                        disabled={Object.keys(quizAnswers).length < currentModuleQuiz.questions.length}
                        className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase text-sm hover:bg-[#B8962E] disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="submit-quiz-btn"
                      >
                        <Check className="w-4 h-4" />
                        Submit Quiz
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <HelpCircle className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-medium mb-2">No Quiz Available</h3>
                  <p className="text-white/50 text-sm">
                    This module doesn't have a quiz yet. Try another module.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Start Business Tab */}
          {activeTab === 'business' && (
            <EntrepreneurshipModule 
              courseId={course.id}
              courseProgress={progressPercent}
            />
          )}
          
          {/* Community Tab */}
          {activeTab === 'community' && (
            <div className="flex-1 overflow-y-auto">
              <CommunityModule 
                userId={userId}
                userName={enrollment?.user_name || userEmail?.split('@')[0] || 'Student'}
                userEmail={userEmail}
              />
            </div>
          )}
          
          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <CourseVideoSection 
                courseId={course.id}
                userId={userId}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 md:p-4 border-t border-white/10 bg-[#0A0A0A]">
          {/* Course Completion Banner */}
          {completedLessons === totalLessons && passedModules.length === course.modules.length && (
            <div className="mb-3 md:mb-4 p-3 bg-green-500/10 border border-green-500/30 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 md:w-6 md:h-6 text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-green-400 font-bold text-sm md:text-base">Course Complete!</p>
                  <p className="text-white/50 text-xs md:text-sm">You've completed all lessons and quizzes.</p>
                </div>
              </div>
              <button
                onClick={handleGetCertificate}
                disabled={generatingCertificate}
                className="px-3 py-2 bg-[#D4AF37] text-black text-xs md:text-sm font-bold uppercase hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2 w-full md:w-auto disabled:opacity-50"
                data-testid="get-certificate-btn"
              >
                {generatingCertificate ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Award className="w-4 h-4" />
                    Get Certificate
                  </>
                )}
              </button>
            </div>
          )}
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 md:gap-6">
              {/* Lesson Progress */}
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white/70 text-xs md:text-sm">{completedLessons}/{totalLessons} lessons</span>
              </div>
              
              {/* Quiz Progress */}
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white/70 text-xs md:text-sm">{passedModules.length}/{course.modules.length} quizzes</span>
              </div>
              
              {/* Overall Progress Bar */}
              <div className="flex items-center gap-2">
                <div className="w-16 md:w-24 h-2 bg-white/10 overflow-hidden">
                  <div 
                    className="h-full bg-[#D4AF37] transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-[#D4AF37] text-xs md:text-sm font-bold">{progressPercent}%</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors w-full md:w-auto"
            >
              Close
            </button>
          </div>
        </div>

        {/* Certificate Modal */}
        {showCertificate && certificateData && (
          <Certificate
            studentName={certificateData.studentName}
            courseName={certificateData.courseName}
            courseId={certificateData.courseId}
            completionDate={certificateData.completionDate}
            certificateId={certificateData.certificateId}
            onClose={() => setShowCertificate(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AcademyModule;
